// Fetch wrapper for the DigiSubs API: cookies, CSRF double submit, current client header, one transparent refresh on
// an expired access token, and typed errors carrying the API's French message.

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const CLIENT_KEY = "ds.clientId";
let currentClientId: string | null = null;
let refreshing: Promise<boolean> | null = null;
let onAuthLost: (() => void) | null = null;

try {
  currentClientId = window.localStorage.getItem(CLIENT_KEY);
} catch {
  currentClientId = null;
}

export function setCurrentClientId(id: string | null): void {
  currentClientId = id;
  try {
    if (id) window.localStorage.setItem(CLIENT_KEY, id);
    else window.localStorage.removeItem(CLIENT_KEY);
  } catch {
    // storage unavailable (private mode): the header still works for this tab
  }
}

export function getCurrentClientId(): string | null {
  return currentClientId;
}

export function setAuthLostHandler(handler: () => void): void {
  onAuthLost = handler;
}

function readCookie(name: string): string | null {
  const match = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

type Query = Record<string, string | number | boolean | null | undefined | Array<string | number>>;

export function buildQuery(query?: Query): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) value.forEach((v) => params.append(key, String(v)));
    else params.set(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

async function refreshSession(): Promise<boolean> {
  if (!refreshing) {
    refreshing = fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
      headers: { "X-CSRF-Token": readCookie("ds_csrf") ?? "" },
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        setTimeout(() => (refreshing = null), 0);
      });
  }
  return refreshing;
}

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
  /** Skip the X-Client-Id header (account level routes). */
  noClient?: boolean;
  raw?: boolean;
}

async function request<T>(method: string, path: string, opts: RequestOptions = {}, retried = false): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const isForm = typeof FormData !== "undefined" && opts.body instanceof FormData;
  if (opts.body !== undefined && !isForm) headers["Content-Type"] = "application/json";
  if (method !== "GET") headers["X-CSRF-Token"] = readCookie("ds_csrf") ?? "";
  if (currentClientId && !opts.noClient) headers["X-Client-Id"] = currentClientId;

  const res = await fetch(`/api${path}${buildQuery(opts.query)}`, {
    method,
    headers,
    credentials: "include",
    body: opts.body === undefined ? undefined : isForm ? (opts.body as FormData) : JSON.stringify(opts.body),
    signal: opts.signal,
  });

  if (res.status === 401 && !retried && !path.startsWith("/auth/login") && !path.startsWith("/auth/refresh")) {
    const ok = await refreshSession();
    if (ok) return request<T>(method, path, opts, true);
    onAuthLost?.();
  }

  if (opts.raw) return res as unknown as T;
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    throw new ApiError(res.status, err?.code ?? "error", err?.message ?? "Une erreur est survenue", err?.details);
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>("GET", path, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("POST", path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("PUT", path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("PATCH", path, { ...opts, body }),
  delete: <T>(path: string, opts?: RequestOptions) => request<T>("DELETE", path, opts),
  /** Multipart upload to /api/files. Returns the stored files. */
  upload: async (files: File[], purpose = "general") => {
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    form.append("purpose", purpose);
    return request<{ items: Array<{ id: string; url: string; name: string; mime: string; size: number; kind: string }> }>(
      "POST",
      "/files",
      { body: form },
    );
  },
  /** Downloads a file response (CSV / PDF exports) and saves it. */
  download: async (path: string, query?: Query, filename = "export.csv") => {
    const res = await request<Response>("GET", path, { query, raw: true });
    if (!res.ok) throw new ApiError(res.status, "download_failed", "Export impossible");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Une erreur est survenue";
}

export function fieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError) || !Array.isArray(err.details)) return {};
  const out: Record<string, string> = {};
  for (const d of err.details as Array<{ path?: string; message?: string }>) {
    if (d.path && d.message && !out[d.path]) out[d.path] = d.message;
  }
  return out;
}
